import { useState } from "react";
import {Link} from "react-router-dom";
import { BASE_URL } from '../apiConfig';
import { useAuth } from "../context/AuthProvider";
import { toast } from 'sonner';
import DeleteModal from '../cors/DeleteModal'; 
import { Headset } from 'lucide-react'

export default function ConfirmatorList({ confirmator, fetchConfirmators }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { token, role, roles } = useAuth();

  const deleteConfirmator = async () => {
    setIsModalOpen(false); 

    try {
      const res = await fetch(BASE_URL+`/api/users/${confirmator.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      })

      if (res.ok) {
        toast.success("Suppression réussie");
        fetchConfirmators('change');
      } else {
        const err = await res.json()
        toast.error("Echec de suppression");
      }
    } catch (error) {
      toast.error("Echec de suppression");
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const formatPhone = (phone) => {
    const phoneStr = phone?.toString() || ""
    return phoneStr.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3")
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat("fr", {
      style: "currency",
      currency: "MAD",
    }).format(parseFloat(price || 0))
  }

  const getActivityStatus = () => {
    if (!confirmator.info.last_login_at || confirmator.info.last_login_at === "") {
      return { 
        status: "Jamais connecté", 
        color: "text-red-600 bg-red-100",
        bg: "bg-red-600",
        tooltip: "Ce vendeur ne s'est jamais connecté"
      };
    }
    
    const lastLogin = new Date(confirmator.info.last_login_at);
    const now = new Date();
    const diffInSeconds = Math.floor((now - lastLogin) / 1000);
    const minutesPassed = Math.floor(diffInSeconds / 60);
    const hoursPassed = Math.floor(minutesPassed / 60);
    const daysPassed = Math.floor(hoursPassed / 24);

    if (diffInSeconds < 60) {
      return {
        status: "En ligne",
        color: "text-green-600 bg-green-100",
        bg: "bg-green-600",
        tooltip: `En ligne maintenant (${lastLogin.toLocaleString()})`
      };
    }
    if (minutesPassed < 60) {
      return {
        status: `En ligne (${minutesPassed} min)`,
        color: "text-green-600 bg-green-100",
        bg: "bg-green-600",
        tooltip: `Actif il y a ${minutesPassed} minutes`
      };
    }
    if (hoursPassed < 24) {
      return {
        status: `En ligne (${hoursPassed}h)`,
        color: "text-blue-600 bg-blue-100",
        bg: "bg-blue-600",
        tooltip: `Actif il y a ${hoursPassed} heures`
      };
    }
    if (daysPassed === 1) {
      return {
        status: "En ligne (1j)",
        color: "text-yellow-600 bg-yellow-100",
        bg: "bg-yellow-600",
        tooltip: `Actif hier (${lastLogin.toLocaleString()})`
      };
    }
    if (daysPassed < 7) {
      return {
        status: `En ligne (${daysPassed}j)`,
        color: "text-amber-600 bg-amber-100",
        bg: "bg-amber-600",
        tooltip: `Actif il y a ${daysPassed} jours`
      };
    }
    if (daysPassed < 30) {
      const weeksPassed = Math.floor(daysPassed / 7);
      return {
        status: weeksPassed === 1 ? "En ligne (1 sem.)" : `En ligne (${weeksPassed} sem.)`,
        color: "text-orange-600 bg-orange-100",
        bg: "bg-orange-600",
        tooltip: `Actif il y a ${weeksPassed} semaine${weeksPassed > 1 ? 's' : ''}`
      };
    }
    return {
      status: "Inactif",
      color: "text-red-600 bg-red-100",
      bg: "bg-red-600",
      tooltip: `Dernière activité il y a plus d'un mois (${lastLogin.toLocaleDateString()})`
    };
  };

  const activity = getActivityStatus();

  return (
    <div className="group bg-white rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-[var(--color-primary-100)]">
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 flex-1">
            <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center">
              <Headset className="w-6 h-6 text-green-600" />
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-3 mb-1">
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-[var(--color-primary-500)] transition-colors duration-300">
                  {confirmator.info?.name || "No name"}
                </h3>
                {
                  confirmator.info.work_status === 1 ?
                  <span className="bg-[var(--color-primary-50)] text-[var(--color-primary-500)] px-3 py-1 rounded-full text-xs font-semibold">
                    {formatPrice(confirmator.info?.price_work)}
                  </span>
                  :
                  <span className="bg-[var(--color-primary-50)] text-[var(--color-primary-500)] px-3 py-1 rounded-full text-xs font-semibold">
                    {formatPrice(confirmator.info?.price_confirm)}
                  </span>
                }
                <span className={`px-3 py-1 rounded-full font-semibold text-[11px] ${activity.color} mb-1`}>
                  {activity.status}
                </span>
              </div>

              <p className="text-gray-600 mb-1 text-xs">{confirmator.email}</p>

              <div className="flex items-center gap-6 text-xs text-gray-500">
                <span>{confirmator.info?.phone ? "+212 "+formatPhone(confirmator.info.phone) : "No phone"}</span>
                <span>RIB: {confirmator.info?.rib || "Not provided"}</span>
              </div>
            </div>
          </div>
          <div className="justify-end">
            <div className="flex items-center gap-2 ml-6">
              {
                (role === 1 || (role === 2 && roles.edit_confirmator === 1)) &&
                  <Link to={`/confirmators/${confirmator.id}`}>
                    <button className="p-2 border-2 cursor-pointer border-gray-200 hover:border-[var(--color-primary-500)] text-gray-600 hover:text-[var(--color-primary-500)] rounded-xl transition-all duration-300">
                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                  </Link>
              }
              {
                (role === 1 || (role === 2 && roles.delete_confirmator === 1)) &&
                  <button 
                    onClick={() => setIsModalOpen(true)} 
                    className="p-2 border-2 border-gray-200 cursor-pointer hover:border-red-400 text-gray-600 hover:text-red-500 rounded-xl transition-all duration-300"
                    title="Voir les détails"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2
                        2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1
                        1 0 00-1-1h-4a1 1 0 00-1 1v3m5 0H6"
                      />
                    </svg>
                  </button>
              }
            </div>
            <span className="text-xs">Joined {formatDate(confirmator.info.created_at)}</span>
          </div>
        </div>
      </div>
        <DeleteModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onConfirm={deleteConfirmator}
          title={`Supprimer le confirmateur`}
          message={`Êtes-vous sûr de vouloir supprimer le confirmateur`}
          itemName={confirmator.info.name}
        />
    </div>
  )
}